import {
  Container,
  Grid,
  Box,
  makeStyles,
  useTheme,
  Slide,
  Fade,
  Grow,
  Typography,
  Avatar,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Loading } from "../../components/shared/loading";
import { TypingQuote } from "../../components/shared/TypingQuote";
import { HeroParticles } from "../../components/shared/HeroParticles";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

const useStyles = makeStyles((theme) => ({
  heroSection: {
    position: "relative",
    overflow: "hidden",
    minHeight: "45vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1a001a 0%, #2d002d 50%, #1a001a 100%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    color: "#ffffff",
    padding: "40px 20px",
    maxWidth: 700,
  },
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes heroExpand": {
    from: { width: 0, opacity: 0 },
    to: { width: 90, opacity: 1 },
  },
  "@keyframes heroShimmerSweep": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },
  heroTitle: {
    fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
    fontWeight: 700,
    marginBottom: 10,
    backgroundImage:
      "linear-gradient(90deg, #ffffff 0%, #e0b8e0 25%, #c77dc7 50%, #e0b8e0 75%, #ffffff 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    animation:
      "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both, $heroShimmerSweep 5s linear infinite",
  },
  heroDivider: {
    width: 90,
    height: 3,
    background:
      "linear-gradient(90deg, transparent, #c77dc7 30%, #e0b8e0 50%, #c77dc7 70%, transparent)",
    margin: "16px auto",
    borderRadius: 2,
    animation: "$heroExpand 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both",
  },
  heroSubtitle: {
    fontSize: "clamp(0.85rem, 1.5vw, 1rem)",
    fontWeight: 400,
    opacity: 0.9,
    maxWidth: 500,
    margin: "0 auto",
    lineHeight: 1.5,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both",
  },
  section: {
    padding: "40px 0",
    background: "#fafafa",
  },
  sectionWhite: {
    padding: "40px 0",
    background: "#ffffff",
  },
  sectionTitle: {
    fontSize: "1.4rem",
    fontWeight: 600,
    color: "#4c004c",
    textAlign: "center",
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: "0.85rem",
    color: "#666666",
    textAlign: "center",
    maxWidth: 450,
    margin: "0 auto 28px",
    lineHeight: 1.5,
  },
  // ── LEADERSHIP CARDS (Executive bio with rotating ring) ──
  "@keyframes leaderEntrance": {
    from: { opacity: 0, transform: "translateY(40px) scale(0.96)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  "@keyframes leaderNameShimmer": {
    "0%, 100%": { backgroundPosition: "0% center" },
    "50%": { backgroundPosition: "100% center" },
  },
  "@keyframes leaderRingRotate": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  "@keyframes leaderRingRotateReverse": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(-360deg)" },
  },
  "@keyframes leaderSparkleDrift1": {
    "0%, 100%": { transform: "translate(0, 0)", opacity: 0.35 },
    "50%": { transform: "translate(8px, -10px)", opacity: 1 },
  },
  "@keyframes leaderSparkleDrift2": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)", opacity: 0.5 },
    "50%": { transform: "translate(-6px, 8px) scale(1.3)", opacity: 1 },
  },
  "@keyframes leaderSparkleDrift3": {
    "0%, 100%": { transform: "translate(0, 0) scale(1)", opacity: 0.25 },
    "50%": { transform: "translate(10px, 5px) scale(1.5)", opacity: 0.9 },
  },
  leaderWrap: {
    position: "relative",
    animation: "$leaderEntrance 0.95s cubic-bezier(0.22, 1, 0.36, 1) both",
    willChange: "transform, opacity",
  },
  leaderCard: {
    position: "relative",
    display: "flex",
    borderRadius: 20,
    overflow: "hidden",
    background: "#ffffff",
    border: "1px solid rgba(76,0,76,0.08)",
    boxShadow: "0 8px 24px rgba(76,0,76,0.08)",
    transition: "all 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
    minHeight: 250,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      minHeight: 0,
    },
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 28px 60px rgba(76,0,76,0.22)",
      borderColor: "rgba(155,59,155,0.28)",
    },
    "&:hover $leaderDivider": {
      width: 64,
    },
    "&:hover $leaderAvatarRing": {
      animationDuration: "3s",
    },
    "&:hover $leaderAvatar": {
      animationDuration: "3s",
      transform: "scale(1.04)",
    },
    "&:hover $leaderPhotoPanel::before": {
      opacity: 1,
    },
  },
  leaderPhotoPanel: {
    position: "relative",
    width: 230,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, #4c004c 0%, #2d002d 60%, #1a001a 100%)",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: { width: 190 },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      padding: "36px 0 32px",
      minHeight: 220,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      width: "180%",
      height: "180%",
      top: "-40%",
      left: "-40%",
      background:
        "radial-gradient(circle at center, rgba(199,125,199,0.4), transparent 50%)",
      filter: "blur(20px)",
      opacity: 0.65,
      transition: "opacity 0.6s ease",
      zIndex: 0,
      pointerEvents: "none",
    },
  },
  leaderSparkle: {
    position: "absolute",
    width: 4,
    height: 4,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.75)",
    boxShadow:
      "0 0 10px rgba(255,255,255,0.9), 0 0 20px rgba(199,125,199,0.6)",
    zIndex: 1,
    pointerEvents: "none",
  },
  leaderSparkle1: {
    top: "18%",
    left: "20%",
    animation: "$leaderSparkleDrift1 5s ease-in-out infinite",
  },
  leaderSparkle2: {
    top: "70%",
    right: "22%",
    animation: "$leaderSparkleDrift2 6.5s ease-in-out infinite 1s",
  },
  leaderSparkle3: {
    top: "32%",
    right: "16%",
    width: 3,
    height: 3,
    animation: "$leaderSparkleDrift3 7s ease-in-out infinite 2s",
  },
  leaderSparkle4: {
    bottom: "24%",
    left: "18%",
    width: 3,
    height: 3,
    animation: "$leaderSparkleDrift1 6s ease-in-out infinite 1.5s",
  },
  leaderAvatarRing: {
    position: "relative",
    width: 162,
    height: 162,
    borderRadius: "50%",
    padding: 4,
    background:
      "conic-gradient(from 0deg, #4c004c, #c77dc7, #ffffff, #9b3b9b, #c77dc7, #4c004c)",
    zIndex: 2,
    animation: "$leaderRingRotate 9s linear infinite",
    transition: "all 0.6s ease",
    boxShadow: "0 0 30px rgba(199,125,199,0.35)",
    [theme.breakpoints.down("sm")]: { width: 138, height: 138 },
  },
  leaderAvatar: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    objectFit: "cover",
    border: "3px solid #ffffff",
    background: "#3a003a",
    boxShadow: "0 8px 28px rgba(0,0,0,0.4)",
    display: "block",
    animation: "$leaderRingRotateReverse 9s linear infinite",
    transition: "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
  },
  leaderContent: {
    flex: 1,
    padding: "32px 36px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
    zIndex: 2,
    [theme.breakpoints.down("sm")]: {
      padding: "28px 26px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "26px 24px 30px",
      textAlign: "center",
      alignItems: "center",
    },
  },
  leaderName: {
    fontSize: "1.3rem",
    fontWeight: 700,
    marginBottom: 4,
    letterSpacing: "-0.01em",
    backgroundImage:
      "linear-gradient(90deg, #1a001a 20%, #4c004c 50%, #9b3b9b 70%, #4c004c 90%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    animation: "$leaderNameShimmer 7s ease-in-out infinite",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.15rem",
    },
  },
  leaderDesig: {
    fontSize: "0.66rem",
    color: "#6b1b6b",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "2px",
    marginBottom: 14,
  },
  leaderDivider: {
    width: 40,
    height: 2,
    background: "linear-gradient(90deg, #4c004c, #9b3b9b)",
    borderRadius: 2,
    marginBottom: 14,
    transition: "width 0.5s cubic-bezier(0.22, 1, 0.36, 1)",
    [theme.breakpoints.down("xs")]: {
      margin: "0 auto 14px",
    },
  },
  leaderDesc: {
    fontSize: "0.82rem",
    color: "#555",
    lineHeight: 1.7,
    letterSpacing: "0.1px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.78rem",
    },
  },

  // ── CORE DEVELOPER CARDS (Clean) ──
  "@keyframes glowRing": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(76,0,76,0)" },
    "50%": { boxShadow: "0 0 0 6px rgba(76,0,76,0.12)" },
  },
  devCard: {
    borderRadius: 16,
    width: "100%",
    background: "#ffffff",
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    border: "1px solid rgba(0,0,0,0.05)",
    transition: "all 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      width: 0,
      height: 3,
      background: "linear-gradient(90deg, #4c004c, #9b3b9b, #4c004c)",
      transition: "width 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    },
    "&:hover": {
      transform: "translateY(-10px)",
      boxShadow: "0 20px 48px rgba(76,0,76,0.14)",
      borderColor: "rgba(76,0,76,0.06)",
    },
    "&:hover::after": {
      width: "100%",
    },
    "&:hover $devAvatarRing": {
      animation: "$glowRing 1.5s ease-in-out infinite",
    },
    "&:hover $devAvatar": {
      borderColor: "rgba(255,255,255,0.5)",
      transform: "scale(1.05)",
    },
  },
  devImgBand: {
    background: "linear-gradient(160deg, #5a0a5a 0%, #4c004c 50%, #3a003a 100%)",
    padding: "32px 20px 36px",
    display: "flex",
    justifyContent: "center",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -18,
      left: "50%",
      transform: "translateX(-50%)",
      width: 0,
      height: 0,
      borderLeft: "20px solid transparent",
      borderRight: "20px solid transparent",
      borderTop: "18px solid #3a003a",
    },
  },
  devSparkle: {
    position: "absolute",
    width: 3,
    height: 3,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.7)",
    boxShadow:
      "0 0 8px rgba(255,255,255,0.9), 0 0 16px rgba(199,125,199,0.6)",
    zIndex: 1,
    pointerEvents: "none",
  },
  devSparkle1: {
    top: "22%",
    left: "16%",
    animation: "$leaderSparkleDrift1 5s ease-in-out infinite",
  },
  devSparkle2: {
    top: "62%",
    right: "20%",
    animation: "$leaderSparkleDrift2 6.5s ease-in-out infinite 1s",
  },
  devSparkle3: {
    top: "30%",
    right: "14%",
    width: 2,
    height: 2,
    animation: "$leaderSparkleDrift3 7s ease-in-out infinite 2s",
  },
  devSparkle4: {
    bottom: "26%",
    left: "12%",
    width: 2,
    height: 2,
    animation: "$leaderSparkleDrift1 6s ease-in-out infinite 1.5s",
  },
  devAvatarRing: {
    borderRadius: "50%",
    padding: 0,
    display: "inline-flex",
    transition: "all 0.4s ease",
  },
  devAvatar: {
    width: 120,
    height: 120,
    borderRadius: "50%",
    objectFit: "cover",
    display: "block",
    border: "4px solid rgba(255,255,255,0.2)",
    boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
    background: "#3a003a",
    transition: "all 0.4s ease",
    [theme.breakpoints.down('sm')]: {
      width: 104,
      height: 104,
    },
  },
  devBody: {
    padding: "26px 22px 24px",
    textAlign: "center",
    [theme.breakpoints.down('sm')]: {
      padding: "24px 18px 20px",
    },
  },
  devName: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: 3,
    letterSpacing: "0.15px",
  },
  devDesig: {
    fontSize: "0.6rem",
    color: "#4c004c",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    marginBottom: 12,
    opacity: 0.75,
  },
  devAccent: {
    width: 20,
    height: 2,
    background: "linear-gradient(90deg, #6b1b6b, #9b3b9b)",
    borderRadius: 2,
    margin: "0 auto 12px",
  },
  devDesc: {
    fontSize: "0.73rem",
    color: "#666",
    lineHeight: 1.7,
    [theme.breakpoints.down('sm')]: {
      fontSize: "0.7rem",
    },
  },
  quoteSection: {
    background: "linear-gradient(135deg, #6b1b6b 0%, #4a0e4a 100%)",
    borderRadius: "12px",
    padding: "28px 24px",
    marginTop: 32,
    textAlign: "center",
  },
  quoteText: {
    fontSize: "0.9rem",
    color: "#ffffff",
    lineHeight: 1.6,
    fontStyle: "italic",
    maxWidth: 700,
    margin: "0 auto 12px",
  },
  quoteAuthor: {
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 600,
    opacity: 0.9,
  },
}));
  
  export const OurTeam = () => {
    const theme = useTheme();
  
    const classes = useStyles();
    const [team1, setTeam1] = useState([]);
    const [team2, setTeam2] = useState([]);
    const [metaTitle, setMetaTitle] = useState("");
    const [metaDesc, setMetaDesc] = useState("");
    const [metaKeywords, setMetaKeywords] = useState("");
    const [isloading, setIsloading] = useState(true);
  
    const handleClick = () => {
      const anchor = document.querySelector("#back-to-top-anchor");
  
      if (anchor) {
        anchor.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
  
    useEffect(() => {
      getMetaData();
    }, []);
  
    const getMetaData = async () => {
      const formData = new FormData();
      formData.append("page", 8);
      await Axios.post("/api/getmetadata", formData, {
        headers: { headKey: headKey },
      }).then((res) => {
        if (res.data.status) {
          setMetaTitle(res.data.data.meta_title);
          setMetaKeywords(res.data.data.meta_keywords);
          setMetaDesc(res.data.data.meta_desc);
          
          
        }
      });
      await  Axios.post("/api/get_all_team", {
        headers: { headKey: headKey },
      }).then((res) => {
        
        if (res.data.status) {
          setTeam1(res.data.team1);
          setTeam2(res.data.team2);
          setIsloading(false);
          handleClick();
          // console.log(res.data.status);
          // setData(ReactHtmlParser(res.data.data.description));
        }
      });
    };
  
      
    return (
      <div>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDesc} />
          <meta name="keywords" content={metaKeywords}></meta>
        </Helmet>
        {/* Hero Section */}
        <Box className={classes.heroSection}>
          <HeroParticles id="ourteam-hero-particles" />
          <Fade in={true} timeout={1000}>
            <Box className={classes.heroContent}>
              <Typography className={classes.heroTitle}>
                Our Team
              </Typography>
              <Box className={classes.heroDivider} />
              <Typography className={classes.heroSubtitle}>
                The Brains Behind Our Breakthroughs
              </Typography>
            </Box>
          </Fade>
        </Box>

        {isloading ? (
          <Loading />
        ) : (
          <>
          {/* Leadership Team Section */}
          <Box className={classes.section}>
            <Container maxWidth="lg">
              <Slide direction="up" in={true} timeout={800}>
                <Box>
                  <Typography className={classes.sectionTitle}>
                    Leadership Team
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Get to know our leaders, their expertise, and the vision that guides us
                  </Typography>
                </Box>
              </Slide>

              <Grid container spacing={3}>
                {team1.map((team, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      className={classes.leaderWrap}
                      style={{ animationDelay: `${index * 150}ms` }}
                    >
                      <Box className={classes.leaderCard}>
                        <Box className={classes.leaderPhotoPanel}>
                          <Box
                            className={`${classes.leaderSparkle} ${classes.leaderSparkle1}`}
                          />
                          <Box
                            className={`${classes.leaderSparkle} ${classes.leaderSparkle2}`}
                          />
                          <Box
                            className={`${classes.leaderSparkle} ${classes.leaderSparkle3}`}
                          />
                          <Box
                            className={`${classes.leaderSparkle} ${classes.leaderSparkle4}`}
                          />
                          <Box className={classes.leaderAvatarRing}>
                            <img
                              src={"/static/team/" + team.image}
                              alt={team.name}
                              className={classes.leaderAvatar}
                            />
                          </Box>
                        </Box>
                        <Box className={classes.leaderContent}>
                          <Typography className={classes.leaderName}>
                            {team.name}
                          </Typography>
                          <Typography className={classes.leaderDesig}>
                            {team.designation}
                          </Typography>
                          <Box className={classes.leaderDivider} />
                          <Typography className={classes.leaderDesc}>
                            {team.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* Core Developers Section */}
          <Box className={classes.sectionWhite}>
            <Container maxWidth="lg">
              <Slide direction="up" in={true} timeout={800}>
                <Box>
                  <Typography className={classes.sectionTitle}>
                    Core Developers
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    Discover the talented developers behind our cutting-edge solutions
                  </Typography>
                </Box>
              </Slide>

              <Grid container spacing={3}>
                {team2.map((team, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Grow in={true} timeout={800 + index * 150}>
                      <Box className={classes.devCard}>
                        <Box className={classes.devImgBand}>
                          <Box
                            className={`${classes.devSparkle} ${classes.devSparkle1}`}
                          />
                          <Box
                            className={`${classes.devSparkle} ${classes.devSparkle2}`}
                          />
                          <Box
                            className={`${classes.devSparkle} ${classes.devSparkle3}`}
                          />
                          <Box
                            className={`${classes.devSparkle} ${classes.devSparkle4}`}
                          />
                          <Box className={classes.devAvatarRing}>
                            <img
                              src={"/static/team/" + team.image}
                              alt={team.name}
                              className={classes.devAvatar}
                            />
                          </Box>
                        </Box>
                        <Box className={classes.devBody}>
                          <Typography className={classes.devName}>
                            {team.name}
                          </Typography>
                          <Typography className={classes.devDesig}>
                            {team.designation}
                          </Typography>
                          <Box className={classes.devAccent} />
                          <Typography className={classes.devDesc}>
                            {team.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grow>
                  </Grid>
                ))}
              </Grid>

              {/* Quote Section */}
              <Fade in={true} timeout={1400}>
                <Box className={classes.quoteSection}>
                  <TypingQuote
                    text={`"At Arawinz, our ethos is rooted in the seamless fusion of creativity and commitment, driving us to provide outstanding results. Our path is defined by an unwavering dedication to surpassing expectations and an earnest endeavor to grasp and cater to our clients' unique requirements. As a collective, we transcend mere collaboration - we are visionaries shaping tomorrow, piecing together a more intelligent and interconnected reality."`}
                    className={classes.quoteText}
                  />
                  <Typography className={classes.quoteAuthor}>
                    - Dr. P. Srinivas Rao, CEO
                  </Typography>
                </Box>
              </Fade>
            </Container>
          </Box>
        </>
        )}
     
      </div>
    );
  };
  

